
import { supabase } from '@/lib/supabase';

type FeedbackType = 'positive' | 'negative' | null;

export const submitFeedback = async (
  messageId: string,
  sessionId: string,
  userId: string,
  feedbackType: FeedbackType,
  contentSample?: string
) => {
  try {
    // If feedback is null (user toggled off), delete any existing feedback
    if (feedbackType === null) {
      const { error } = await supabase
        .from('assistant_feedback')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', userId);
        
      return { success: !error, error };
    }
    
    // Normalize and prepare content sample
    let truncatedContent = contentSample;
    
    if (contentSample) {
      // Preserve Unicode characters while normalizing text
      // This is especially important for languages like Vietnamese
      truncatedContent = contentSample
        .normalize('NFC')
        .substring(0, 150);
      
      // Add ellipsis if truncated
      if (contentSample.length > 150) {
        truncatedContent += '...';
      }
    }
    
    // Insert or update feedback
    const { data, error } = await supabase
      .from('assistant_feedback')
      .upsert(
        {
          user_id: userId,
          message_id: messageId,
          session_id: sessionId,
          feedback_type: feedbackType,
          content_sample: truncatedContent,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'message_id,user_id' }
      );
    
    // If feedback was added successfully, check if this message was created by a rule
    if (!error) {
      await updateRuleEffectiveness(messageId, feedbackType === 'positive');
    }
    
    return { success: !error, error, data };
  } catch (err) {
    console.error('Error submitting feedback:', err);
    return { success: false, error: err };
  }
};

export const getFeedbackForMessages = async (messageIds: string[], userId: string) => {
  if (!messageIds.length) return {};
  
  const { data, error } = await supabase
    .from('assistant_feedback')
    .select('message_id, feedback_type')
    .eq('user_id', userId)
    .in('message_id', messageIds);
  
  if (error) {
    console.error('Error fetching feedback:', error);
    return {};
  }
  
  // Convert to a map of message_id -> feedback_type
  return data.reduce((acc, item) => {
    acc[item.message_id] = item.feedback_type;
    return acc;
  }, {} as Record<string, 'positive' | 'negative'>);
};

// Track when a rule is used in a response
export const trackRuleUsage = async (
  ruleId: number,
  messageId: string,
  sessionId: string,
  userId: string,
  wasEffective: boolean = true
) => {
  try {
    const { error } = await supabase
      .from('assistant_rule_usage')
      .insert({
        rule_id: ruleId,
        message_id: messageId,
        session_id: sessionId,
        user_id: userId,
        was_effective: wasEffective,
      });
    
    if (error) {
      console.error('Error tracking rule usage:', error);
    }
    
    return { success: !error };
  } catch (err) {
    console.error('Error in trackRuleUsage:', err);
    return { success: false };
  }
};

// Update rule effectiveness based on feedback
export const updateRuleEffectiveness = async (
  messageId: string,
  wasEffective: boolean
) => {
  try {
    // First check if this message was created using a rule
    const { data: ruleUsage, error: fetchError } = await supabase
      .from('assistant_rule_usage')
      .select('id')
      .eq('message_id', messageId)
      .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors
      
    if (fetchError) {
      console.error('Error checking rule usage:', fetchError);
      return { success: false };
    }
    
    // If no rule was used for this message, nothing to update
    if (!ruleUsage) {
      return { success: true };
    }
    
    // Update the rule usage with the feedback effectiveness
    const { error } = await supabase
      .from('assistant_rule_usage')
      .update({ was_effective: wasEffective })
      .eq('message_id', messageId);
    
    if (error) {
      console.error('Error updating rule effectiveness:', error);
    }
    
    return { success: !error };
  } catch (err) {
    console.error('Error in updateRuleEffectiveness:', err);
    return { success: false };
  }
};
